import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import '../assets/css/wordmatch.css';
import Sidebar from '../components/Sidebar';
import GameMenu from '../components/GameMenu';
import Timer from '../components/Timer';
import Popup from '../components/Popup';
import LeaveGamePopup from '../components/LeaveGamePopup';
import EndGameBtn from "../components/EndGameBtn";
// import AnimatedBg from "../components/AnimatedBg";
import api from "../api/client";

// Tous les modes disponibles
const MODES = [
  { value: "sentence",    label: "Phrases à trous" },
  { value: "synonym",     label: "Synonymes" },
  { value: "antonym",     label: "Antonymes" },
  { value: "translation", label: "Traduction" },
  { value: "definition",  label: "Définition" },
];

const shuffle = (array) => [...array].sort(() => Math.random() - 0.5);

// FIX : uniqueId sorti du composant pour éviter une re-création à chaque render
let idCount = 0;
const uniqueId = () => `id-${idCount++}`;

export default function WordMatch() {
  const location = useLocation();
  const { theme, subTheme, level = "B1" } = location.state || {};

  const [timerIsFinished, setTimerIsFinished] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);

  const sendTime = (time) => {
    setTimeElapsed(time);
  };

  const getTimerTimeRef = useRef(null);

  const [leaveGamePopup, setLeaveGamePopup] = useState(false);

  const sendLeaveGame = (value) => {
    setLeaveGamePopup(value);
  };

  // ── Modes sélectionnés (multi-sélection) ─────────────────────────────────
  // Par défaut : sentence + synonym + translation (comme avant la BDD)
  const [selectedModes, setSelectedModes] = useState(["sentence", "synonym", "translation"]);

  //  Bascule un mode on/off (garde toujours au moins 1 mode actif)
  const toggleMode = (modeValue) => {
    setSelectedModes(prev => {
      if (prev.includes(modeValue)) {
        if (prev.length === 1) return prev; // toujours au moins 1
        return prev.filter(m => m !== modeValue);
      }
      return [...prev, modeValue];
    });
  };

  // ── Chargement depuis l'API ──────────────────────────────────────
  const [wordPairs, setWordPairs] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]); // [{ mode, label }]
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setApiError(null);
    setWordPairs([]);
    setMatched([]);
    setErrorCount(0);

    //  Un seul appel avec tous les modes sélectionnés
    const params = new URLSearchParams({
      level,
      n: 8,
      modes: selectedModes.join(","),
    });
    if (theme)    params.append("theme", theme);
    if (subTheme) params.append("sous_theme", subTheme);

    api.get(`/api/games/word-match/?${params}`)
      .then(({ data }) => {
        // data.words   = [{ id, term }]
        // data.columns = [{ mode, label, items: [{ id, value }] }]

        const pairs = data.words.map(w => {
          const pair = { en: w.term };
          data.columns.forEach(col => {
            const match = col.items.find(item => item.id === w.id);
            pair[col.mode] = match ? match.value : "";
          });
          return pair;
        }).filter(pair =>
          // Exclure les paires où toutes les colonnes droites sont vides
          data.columns.some(col => pair[col.mode] && pair[col.mode].trim() !== "")
        );

        setWordPairs(pairs);
        setColumnDefs(data.columns.map(col => ({ mode: col.mode, label: col.label })));
        setLoading(false);
      })
      .catch((err) => {
        setApiError(err.message);
        setLoading(false);
      });
  }, [theme, subTheme, level, selectedModes]);
  // ────────────────────────────────────────────────────────────────

  const baseKey = 'en';
  const [columns, setColumns] = useState([]);
  const [selections, setSelections] = useState({});
  const [matched, setMatched] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [showErrorStyle, setShowErrorStyle] = useState(false);
  const [allWords, setAllWords] = useState({});

  useEffect(() => {
    // FIX : Guard indispensable — wordPairs est vide pendant le chargement API
    if (wordPairs.length === 0 || columnDefs.length === 0) return;

    const baseColumn = {
      label: "Anglais",
      key: baseKey,
      words: shuffle(wordPairs.map(pair => ({
        value:   pair[baseKey],
        pairKey: pair[baseKey],
        id:      uniqueId(),
      }))),
    };

    //  Une colonne par mode retourné par l'API
    const extraColumns = columnDefs.map(colDef => ({
      label: colDef.label,
      key:   colDef.mode,
      words: shuffle(wordPairs.map(pair => ({
        value:   pair[colDef.mode] || "",
        pairKey: pair[baseKey],
        id:      uniqueId(),
      }))),
    }));

    const allCols = [baseColumn, ...extraColumns];
    setColumns(allCols);

    const wordsByKey = {};
    allCols.forEach(col => { wordsByKey[col.key] = col.words; });
    setAllWords(wordsByKey);
  }, [wordPairs, columnDefs]);

  const isMatched = (id) => matched.some(group => Object.values(group.ids).includes(id));

  const handleSelect = (colKey, word) => {
    if (isMatched(word.id)) return;

    setSelections(prev => {
      const updated = { ...prev, [colKey]: word };

      if (Object.keys(updated).length === columns.length) {
        const pairKey = updated[baseKey].pairKey;
        const allMatch = Object.values(updated).every(w => w.pairKey === pairKey);
        const ids = Object.fromEntries(columns.map(col => [col.key, updated[col.key].id]));

        if (allMatch) {
          setMatched(prev => {
            const alreadyExists = prev.some(group =>
              JSON.stringify(group.ids) === JSON.stringify(ids)
            );
            if (!alreadyExists) {
              return [...prev, { ids, pairKey }];
            }
            return prev;
          });
        } else {
          setErrorCount(prev => prev + 1);
          setFeedback(prev => [...prev, { ids, success: false }]);
          setShowErrorStyle(true);
          setTimeout(() => setShowErrorStyle(false), 1000);
          setTimeout(() => {
            setFeedback(prev => prev.filter(f => f.ids[baseKey] !== ids[baseKey]));
          }, 1000);
        }

        return {};
      }

      return updated;
    });
  };

  const getButtonStyle = (id) => {
    if (isMatched(id)) {
      return {
        backgroundColor: "var(--orange)",
        borderColor: "var(--orange)",
        color: "white"
      };
    }
    if (Object.values(selections).some(sel => sel.id === id)) {
      return {
        borderColor: "var(--orange)",
        color: "var(--orange)"
      };
    }
    const isError = feedback.some(f => Object.values(f.ids).includes(id));
    if (isError) {
      return {
        backgroundColor: "#A8AEBE",
        color: "white"
      };
    }
    return {};
  };

  useEffect(() => {
    if (!timerIsFinished && wordPairs.length > 0 && matched.length === wordPairs.length) {
      if (getTimerTimeRef.current) {
        try {
          const currentTime = getTimerTimeRef.current();
          sendTime(currentTime);
        } catch (e) {
          console.warn("Impossible de récupérer le temps depuis le Timer :", e);
        }
      }
      setTimerIsFinished(true);
    }
  }, [matched, timerIsFinished, wordPairs.length]);

  // FIX : matched.length au lieu de matched (array vs number)
  const checkErrors = useCallback(() => {
    if (matched.length !== wordPairs.length) {
      const errorsToAdd = wordPairs.length - matched.length;
      setErrorCount((prev) => prev + errorsToAdd);
    }
  }, [matched.length, wordPairs.length]);

  return (
    <div className="page-container">
      <Sidebar />
      <div className="content-container inGame" id="wordMatch">

        {loading ? (
          <div className="loading-container">
            <h1>Chargement...</h1>
          </div>
        ) : apiError ? (
          <div className="error-container">
            <h2>Une erreur est survenue lors du chargement des données.</h2>
            <button className="mode-btn active" onClick={() => window.location.reload()}>
              Réessayer
            </button>
          </div>
        ) : (
          <>
            <div className="title">
              <h4>{theme} - {subTheme}</h4>
              <div className="level-infos">
                <Timer
                  timer={timerIsFinished}
                  onFinish={() => {
                    checkErrors();
                    setTimerIsFinished(true);
                  }}
                  onSendTime={sendTime}
                  onGetTime={(getTimeFn) => getTimerTimeRef.current = getTimeFn}
                  hasLimit={false}
                />
              </div>
            </div>

            {/* ── Sélecteur de modes (multi-sélection) ── */}
            <div className="mode-selector">
              {MODES.map(m => (
                <button
                  key={m.value}
                  className={`mode-btn ${selectedModes.includes(m.value) ? "active" : ""}`}
                  onClick={() => toggleMode(m.value)}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <div className={`game-container launched level-${level}`}>
              <h3>Associez les mots entre eux</h3>
              <div className={`game-content ${showErrorStyle ? 'show-error' : ''}`}>

                <div className="columns-container">
                  <div className="columns-remaining">
                    {columns.map(col => {
                      const remaining = col.words.filter(w => !isMatched(w.id));
                      const matchedWords = matched
                        .map(pair => {
                          const wordId = pair.ids[col.key];
                          return allWords[col.key]?.find(w => w.id === wordId);
                        })
                        .filter(Boolean);

                      return (
                        <div key={col.key} className="column-type">
                          <h3>{col.label}</h3>
                          <div className="words-container">
                            {remaining.map(word => (
                              <div
                                key={word.id}
                                className="words"
                                style={getButtonStyle(word.id)}
                                onClick={() => handleSelect(col.key, word)}
                              >
                                {word.value}
                              </div>
                            ))}
                            {matchedWords.map(word => (
                              <div
                                key={word.id}
                                className="words matched"
                                style={getButtonStyle(word.id)}
                              >
                                {word.value}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="wrong-answers" style={{ display: "none" }}>
                <h2>Erreurs : {errorCount}</h2>
              </div>
            </div>

            <EndGameBtn onClick={() => {
              if (getTimerTimeRef.current) {
                const currentTime = getTimerTimeRef.current();
                sendTime(currentTime);
              }
              checkErrors();
              setTimerIsFinished(true);
            }} />
          </>
        )}
      </div>

      {/* <AnimatedBg /> */}
      <GameMenu onSendLeaveGame={sendLeaveGame} />
      <Popup time={timeElapsed} timerIsFinished={timerIsFinished} errorCount={errorCount} />
      <LeaveGamePopup popupOpen={leaveGamePopup} onSendLeaveGame={sendLeaveGame} />
    </div>
  );
}